import { TaskStatusEnum } from "../../../Enums/TaskStatuses";
import {
  isTaskContentDTO,
  TaskContentDTO,
  TaskContentType,
} from "../../ClientRequest/TaskContentDTO";
import { isTaskIdResponseDTO } from "./TaskId";
import { TaskIllustrationDTOType } from "./TaskIllustration";
import { TaskModules } from "./TaskModules";
import { TaskTags } from "./TaskTags";

export interface TaskPropApi {
  taskModificationTime: Date;
  taskCreationTime: Date;
  taskPrivateName: string;
  taskPublicName: string;
  taskStatusId: number;
  taskCost: number;
  taskId?: string;
}

export class TaskPropImpl implements TaskPropApi {
  taskPrivateName: string;
  taskPublicName: string;
  taskStatusId: number;
  taskCost: number;
  taskModificationTime: Date;
  taskCreationTime: Date;
  taskId?: string;

  constructor(taskData: TaskPropApi) {
    this.taskPrivateName = taskData.taskPrivateName;
    this.taskPublicName = taskData.taskPublicName;
    this.taskStatusId = taskData.taskStatusId;
    this.taskCost = taskData.taskCost;
    this.taskId = taskData.taskId;
    this.taskCreationTime = taskData.taskCreationTime;
    this.taskModificationTime = taskData.taskModificationTime;
  }
}

export class EmptyTaskPropImpl extends TaskPropImpl {
  constructor() {
    super({
      taskPrivateName: "",
      taskPublicName: "",
      taskStatusId: TaskStatusEnum.InDevelopment,
      taskCost: 0,
      taskId: "",
      taskCreationTime: new Date(),
      taskModificationTime: new Date(),
    });
  }
}

export type TaskResponseApi = TaskPropApi & {
  taskContent: TaskContentDTO;
};

export let isTaskWithContentResponseDTO = (
  variable: unknown
): variable is TaskResponseApi => {
  return (
    isTaskIdResponseDTO(variable) &&
    "taskModificationTime" in variable &&
    typeof variable.taskModificationTime === "string" &&
    "taskCreationTime" in variable &&
    typeof variable.taskCreationTime === "string" &&
    "taskPrivateName" in variable &&
    typeof variable.taskPrivateName === "string" &&
    "taskPublicName" in variable &&
    typeof variable.taskPublicName === "string" &&
    "taskStatusId" in variable &&
    typeof variable.taskStatusId === "number" &&
    "taskCost" in variable &&
    typeof variable.taskCost === "number" &&
    "taskContent" in variable &&
    isTaskContentDTO(variable.taskContent)
  );
};

export let isTaskWithNameAndCostAndContentResponseDTO = (
  variable: unknown
): variable is TaskResponseApi => {
  return (
    isTaskIdResponseDTO(variable) &&
    "taskPublicName" in variable &&
    typeof variable.taskPublicName === "string" &&
    "taskCost" in variable &&
    typeof variable.taskCost === "number" &&
    "taskContent" in variable &&
    isTaskContentDTO(variable.taskContent)
  );
};

// Task from response
export interface TaskWithTagsAndIllustrationsAndModulesResponseApi
  extends TaskPropApi {
  taskModules: TaskModules[];
  taskTags: TaskTags[];
  taskContent: unknown;
  taskIllustrations: TaskIllustrationDTOType[];
}

export class TaskWithTagsAndIllustrationsAndModulesResponseImpl
  extends TaskPropImpl
  implements TaskWithTagsAndIllustrationsAndModulesResponseApi
{
  taskModules: TaskModules[];
  taskTags: TaskTags[];
  taskIllustrations: TaskIllustrationDTOType[];
  taskContent: unknown;

  constructor(taskData: TaskWithTagsAndIllustrationsAndModulesResponseApi) {
    super(taskData);
    this.taskModules = taskData.taskModules;
    this.taskTags = taskData.taskTags;
    this.taskIllustrations = taskData.taskIllustrations;
    this.taskContent = taskData.taskContent;
  }
}

export class EmptyTaskWithTagsAndIllustrationsAndModulesResponseImpl extends TaskWithTagsAndIllustrationsAndModulesResponseImpl {
  constructor() {
    let emptyTask = { ...new EmptyTaskPropImpl() };
    super({
      taskModules: [],
      taskTags: [],
      taskContent: new TaskContentDTO({} as TaskContentType),
      taskIllustrations: [],
      ...emptyTask,
    });
  }
}

export type TaskWithContent =
  TaskWithTagsAndIllustrationsAndModulesResponseApi & {
    taskContent: TaskContentDTO;
  };

export let isTaskWithTagsAndIllustrationsWithContentResponseDTO = (
  variable: unknown
): variable is TaskWithContent => {
  return (
    isTaskWithContentResponseDTO(variable) &&
    "taskTags" in variable &&
    Array.isArray(variable.taskTags) &&
    "taskIllustrations" in variable &&
    Array.isArray(variable.taskIllustrations) &&
    "taskModules" in variable &&
    Array.isArray(variable.taskModules)
  );
};
